import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogActions,
  Button,
} from "@mui/material";
import * as strings from "../strings";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  ServiceWindow,
  SmartSchedulingFormValues,
} from "../models/smart-scheduling-options";
import BaseModal from "./BaseModal";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import { DbWrite } from "../database";
import { createToastMessageID } from "../utils";
import { logger } from "../logging";
import { useToastMessageStore } from "../store/toast-messages";
import {
  getReadableTaskType,
  getTaskSpecificDetailsString,
} from "../models/task-types";
import BaseInputNumberFloat from "./BaseInputNumberFloat";
import CircularProgress from "@mui/material/CircularProgress";
import BaseButtonSecondary from "./BaseButtonSecondary";
import BaseButtonPrimary from "./BaseButtonPrimary";
import { CompactHoursMinutesInput } from "./CompactHoursMinutesInput";

interface SmartSchedulingSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  initialValues: SmartSchedulingFormValues;
  siteKey: string;
  validTaskTypes: number[];
}

interface TaskTypeOption {
  taskType: number;
  taskTypeString: string;
  readableTaskType: string;
  serviceWindowCount: number;
}

export function SmartSchedulingSettingsDialog({
  open,
  onClose,
  initialValues,
  siteKey,
  validTaskTypes,
}: SmartSchedulingSettingsDialogProps) {
  const [smartSchedulingValues, setSmartSchedulingValues] =
    useState<SmartSchedulingFormValues>(initialValues);
  const addMessage = useToastMessageStore((state) => state.addToastMessage);

  const [isTimePickerOpen, setTimePickerOpen] = useState(false);
  const [startHour, setStartHour] = useState<number>(8);
  const [endHour, setEndHour] = useState<number>(10);
  const [selectedTaskType, setSelectedTaskType] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const hours = Array.from({ length: 23 }, (_, i) => i + 0);
  const [activeTab, setActiveTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTaskTypeOption, setSelectedTaskTypeOption] =
    useState<TaskTypeOption | null>(null);

  // Create task type options with service window counts
  const taskTypeOptions: TaskTypeOption[] = useMemo(() => {
    return validTaskTypes
      .map((taskType) => {
        const taskTypeString = getTaskSpecificDetailsString(taskType);
        const serviceWindows =
          smartSchedulingValues.defaultServiceWindowsByTaskType[
            taskTypeString
          ] || [];
        return {
          taskType,
          taskTypeString,
          readableTaskType: getReadableTaskType(taskType),
          serviceWindowCount: serviceWindows.length,
        };
      })
      .sort((a, b) => a.readableTaskType.localeCompare(b.readableTaskType));
  }, [validTaskTypes, smartSchedulingValues.defaultServiceWindowsByTaskType]);

  const formatHour = (hour: number): string => {
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${period}`;
  };

  const handleAddServiceWindow = (
    taskType: string,
    startHour: number,
    endHour: number,
  ) => {
    const humanReadableString = `${formatHour(startHour)} - ${formatHour(endHour)}`;
    const newServiceWindow: ServiceWindow = {
      startHour,
      endHour,
      humanReadableString,
    };

    setSmartSchedulingValues({
      ...smartSchedulingValues,
      defaultServiceWindowsByTaskType: {
        ...smartSchedulingValues.defaultServiceWindowsByTaskType,
        [taskType]: [
          ...(smartSchedulingValues.defaultServiceWindowsByTaskType[taskType] ||
            []),
          newServiceWindow,
        ],
      },
    });
  };

  async function handleUpdateSmartScheduling(
    values: SmartSchedulingFormValues,
  ) {
    try {
      const updateData = {
        customizations: {
          smartSchedulingSettings: values,
        },
      };
      await DbWrite.siteKey.editCustomizations(siteKey, updateData);

      setSmartSchedulingValues(values);

      addMessage({
        id: createToastMessageID(),
        message: "Smart scheduling settings updated successfully",
        dialog: false,
        type: "success",
      });
      onClose();
    } catch (error) {
      logger.error(
        `An error occurred updating smart scheduling settings`,
        error,
      );
      addMessage({
        id: createToastMessageID(),
        message: strings.UNEXPECTED_ERROR,
        dialog: false,
        type: "error",
      });
    }
  }

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await handleUpdateSmartScheduling(smartSchedulingValues);
    } catch (error) {
      logger.error(
        `An error occurred while saving smart scheduling settings`,
        error,
      );
      addMessage({
        id: createToastMessageID(),
        message: strings.UNEXPECTED_ERROR,
        dialog: false,
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const sortServiceWindows = (windows: ServiceWindow[]): ServiceWindow[] => {
    return [...windows].sort((a, b) => a.startHour - b.startHour);
  };

  const handleWorkDurationChange = (taskType: string, duration: string) => {
    const durationNumber = parseFloat(duration) || 0;
    setSmartSchedulingValues({
      ...smartSchedulingValues,
      defaultWorkDurationByTaskType: {
        ...smartSchedulingValues.defaultWorkDurationByTaskType,
        [taskType]: durationNumber,
      },
    });
  };

  const serviceWindowsContent = (
    <>
      <div className="mx-8 my-8">
        <FormControl fullWidth className="mb-8">
          <InputLabel>Task Type</InputLabel>
          <Select
            value={selectedTaskTypeOption?.taskTypeString || ""}
            label="Task Type"
            onChange={(e) => {
              const option = taskTypeOptions.find(
                (opt) => opt.taskTypeString === e.target.value,
              );
              setSelectedTaskTypeOption(option || null);
            }}
          >
            {taskTypeOptions.map((option) => (
              <MenuItem
                key={option.taskTypeString}
                value={option.taskTypeString}
              >
                <div className="flex w-full justify-between">
                  <span>{option.readableTaskType}</span>
                  <span className="ml-4 text-gray-500">
                    ({option.serviceWindowCount} service window
                    {option.serviceWindowCount !== 1 ? "s" : ""})
                  </span>
                </div>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedTaskTypeOption && (
          <div className="mb-8">
            <div className="mb-4 flex items-center justify-between">
              <Typography variant="h6">
                {selectedTaskTypeOption.readableTaskType}
              </Typography>
              {!isTimePickerOpen && (
                <BaseButtonSecondary
                  onClick={() => {
                    setTimePickerOpen(true);
                    setSelectedTaskType(selectedTaskTypeOption.taskTypeString);
                  }}
                >
                  Add Service Window
                </BaseButtonSecondary>
              )}
            </div>

            {isTimePickerOpen &&
              selectedTaskType === selectedTaskTypeOption.taskTypeString && (
                <div className="space-y-4">
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Start Time</InputLabel>
                        <Select
                          value={startHour}
                          label="Start Time"
                          onChange={(e) => {
                            setStartHour(Number(e.target.value));
                            setError("");
                          }}
                        >
                          {hours.map((hour) => (
                            <MenuItem key={hour} value={hour}>
                              {formatHour(hour)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>End Time</InputLabel>
                        <Select
                          value={endHour}
                          label="End Time"
                          onChange={(e) => {
                            setEndHour(Number(e.target.value));
                            setError("");
                          }}
                        >
                          {hours.map((hour) => (
                            <MenuItem key={hour} value={hour}>
                              {formatHour(hour)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  {error && <FormHelperText error>{error}</FormHelperText>}
                  <div className="flex justify-end space-x-2">
                    <button
                      className="text-gray-600 hover:text-gray-800"
                      onClick={() => {
                        setTimePickerOpen(false);
                        setSelectedTaskType(null);
                      }}
                    >
                      {strings.buttons.CANCEL}
                    </button>
                    <BaseButtonPrimary
                      onClick={() => {
                        if (endHour <= startHour) {
                          setError("End time must be after start time");
                          return;
                        }
                        if (endHour - startHour > 12) {
                          setError("Time range cannot exceed 12 hours");
                          return;
                        }
                        handleAddServiceWindow(
                          selectedTaskTypeOption.taskTypeString,
                          startHour,
                          endHour,
                        );
                        setTimePickerOpen(false);
                        setSelectedTaskType(null);
                      }}
                    >
                      {strings.buttons.ADD}
                    </BaseButtonPrimary>
                  </div>
                </div>
              )}

            <TableContainer component={Paper} className="mt-4">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Time Window</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortServiceWindows(
                    smartSchedulingValues.defaultServiceWindowsByTaskType[
                      selectedTaskTypeOption.taskTypeString
                    ] || [],
                  ).map((window) => (
                    <TableRow key={window.humanReadableString}>
                      <TableCell>{window.humanReadableString}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => {
                            const updatedWindows = (
                              smartSchedulingValues
                                .defaultServiceWindowsByTaskType[
                                selectedTaskTypeOption.taskTypeString
                              ] || []
                            ).filter(
                              (w) =>
                                w.humanReadableString !==
                                window.humanReadableString,
                            );
                            setSmartSchedulingValues({
                              ...smartSchedulingValues,
                              defaultServiceWindowsByTaskType: {
                                ...smartSchedulingValues.defaultServiceWindowsByTaskType,
                                [selectedTaskTypeOption.taskTypeString]:
                                  updatedWindows,
                              },
                            });
                          }}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!smartSchedulingValues.defaultServiceWindowsByTaskType[
                    selectedTaskTypeOption.taskTypeString
                  ]?.length && (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No service windows configured
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}

        {!selectedTaskTypeOption && (
          <Typography
            variant="body2"
            color="textSecondary"
            className="mt-8 text-center"
          >
            Select a task type to configure service windows
          </Typography>
        )}
      </div>
    </>
  );

  const defaultDurationsContent = (
    <div className="mx-8 my-4">
      <Typography variant="h6" className="mb-4">
        Set Default Work Duration
      </Typography>
      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: 1 }}>Task Type</TableCell>
              <TableCell sx={{ py: 1 }}>Default Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {validTaskTypes.map((taskType) => {
              const taskTypeString = getTaskSpecificDetailsString(taskType);
              return (
                <TableRow key={taskTypeString}>
                  <TableCell sx={{ py: 1 }}>
                    {getReadableTaskType(taskType)}
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <CompactHoursMinutesInput
                      value={
                        smartSchedulingValues.defaultWorkDurationByTaskType[
                          taskTypeString
                        ] || 0
                      }
                      onChange={(minutes) =>
                        handleWorkDurationChange(
                          taskTypeString,
                          minutes.toString(),
                        )
                      }
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );

  const modalContent = (
    <>
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        className="mx-8 mt-4"
      >
        <Tab label="Service Windows" />
        <Tab label="Default Durations" />
      </Tabs>

      {activeTab === 0 && serviceWindowsContent}
      {activeTab === 1 && defaultDurationsContent}

      <div className="mt-8 flex justify-end px-8 pb-8">
        <BaseButtonSecondary
          onClick={onClose}
          disabled={isSaving}
          className="mr-2"
        >
          Cancel
        </BaseButtonSecondary>
        <BaseButtonPrimary
          onClick={handleSave}
          isBusy={isSaving}
          className="relative"
        >
          {isSaving && (
            <CircularProgress
              size={24}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            />
          )}
          <span className={isSaving ? "invisible" : ""}>Save Changes</span>
        </BaseButtonPrimary>
      </div>
    </>
  );

  const header = (
    <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white ">
      <h1 className="inline-flex items-center text-xl font-semibold ">
        {strings.SMART_SCHEDULING_SETTINGS}
      </h1>
      <button type="button" onClick={() => onClose()}>
        <XMarkIcon
          aria-label="close new requirement form"
          className="h-6 text-white"
        />
      </button>
    </div>
  );

  return (
    <BaseModal
      open={open}
      closeModal={onClose}
      title={header}
      parentDivStyles="inline-block transform overflow-hidden  max-w-screen-sm rounded-lg bg-white text-left align-middle shadow-xl transition-all"
    >
      {modalContent}
    </BaseModal>
  );
}
